import styled from "@emotion/styled"
import { Container, css } from "@mui/material"
import * as api from "api"
import SeoMetaData from "common/SeoMetaData"
import slugify from "common/utils/slugify"
import About from "components/ServiceCenter/About"
import Details from "components/ServiceCenter/Details"
import Gallery from "components/ServiceCenter/Gallery"
import Headline from "components/ServiceCenter/Headline"
import Promotions from "components/ServiceCenter/Promotions"
import Services from "components/ServiceCenter/Services"
import { GetStaticPaths, GetStaticProps } from "next"

const baseStyledRow = (p) => css`
  display: flex;
  flex-direction: column;
  ${p.theme.breakpoints.up("sm")} {
    flex-direction: row;
    padding: 30px 0;
    > div {
      flex-basis: 0;
      flex-grow: 1;
      padding: 0 25px;
      &:last-child:not(:first-child) {
        border-right: 1px solid ${p.theme.colors.silver};
      }
    }
  }
`
const Styled = {
  Row: styled.div`
    ${baseStyledRow}
  `,
  ColoredRow: styled.div`
    ${baseStyledRow}
    background-color: ${(p) => p.theme.colors.paleGreyTwo};
  `,
}

type ServiceCenterStaticProps = {
  serviceCenterId: string
  serviceCenterSlug: string
}

export const getStaticPaths: GetStaticPaths<
  ServiceCenterStaticProps
> = async () => {
  try {
    const data = (await api
      .getServiceCenter()
      .then(api.extractData)) as (api.SingleServiceCenter & api.ServiceCenter)[]
    const paths = data.map((serviceCenter) => ({
      params: {
        serviceCenterId: serviceCenter.id.toString(),
        serviceCenterSlug: slugify(serviceCenter.name),
      },
    }))
    return { paths, fallback: "blocking" }
  } catch (err) {
    console.error(
      "Failed to load service centers for static paths:",
      err instanceof Error ? err.message : err,
    )
    return { paths: [], fallback: "blocking" }
  }
}

type ServiceCenterProps = {
  serviceCenter: api.SingleServiceCenter & api.ServiceCenter
}

export const getStaticProps: GetStaticProps<
  ServiceCenterProps,
  ServiceCenterStaticProps
> = async ({ params }) => {
  let serviceCenter: api.SingleServiceCenter & api.ServiceCenter

  try {
    serviceCenter = (await api
      .getServiceCenterId(params.serviceCenterId)
      .then(api.extractData)) as api.SingleServiceCenter & api.ServiceCenter
  } catch (err) {
    console.error(
      `Failed to load service center ${params.serviceCenterId}:`,
      err instanceof Error ? err.message : err,
    )
    return { notFound: true, revalidate: 60 }
  }

  if (!serviceCenter) {
    return { notFound: true, revalidate: 60 }
  }

  return { props: { serviceCenter }, revalidate: 60 }
}

const ServiceCenter = ({ serviceCenter }: ServiceCenterProps) => {
  return (
    <>
      <SeoMetaData
        data={{
          title: `${serviceCenter.name} - ${serviceCenter.city}`,
          description: serviceCenter.description
            ?.replace(/(\r\n|\n|\r)/gm, "")
            .substring(0, 150),
          image: serviceCenter.logo,
          keywords: "",
        }}
      />
      <Container disableGutters>
        <Styled.Row>
          <Headline serviceCenter={serviceCenter} />
          <Details serviceCenter={serviceCenter} />
        </Styled.Row>
        <Styled.ColoredRow>
          <About serviceCenter={serviceCenter} />
          <Services serviceCenter={serviceCenter} />
        </Styled.ColoredRow>
        <Styled.Row>
          <Gallery serviceCenter={serviceCenter} />
        </Styled.Row>
        <Styled.Row>
          <Promotions serviceCenter={serviceCenter} />
        </Styled.Row>
      </Container>
    </>
  )
}

export default ServiceCenter
